// crewbit-admin/src/utils/scheduleViewOptions.ts
//
// Schedule Redesign v1 — pure helpers for Settings -> Schedule defaults.
// Returns split option lists for zoom and lens per surface.
//
// Industry Engine Phase 1c — the inherited zoom/lens + route gate now come from
// the cluster resolver (`ResolvedClusterConfig`) instead of the dropped
// `schedule_industry_defaults` table. Body byte-equivalent to mobile modulo
// import path.

import type { ResolvedClusterConfig } from '../data/resolveClusterConfig';

export type ScheduleZoomOptionValue =
  | 'inherit'
  | 'today'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

export type ScheduleLensOptionValue =
  | 'inherit'
  | 'calendar'
  | 'crew'
  | 'route';

export interface ScheduleViewOption {
  value: string;
  label: string;
  subtitle?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export interface ScheduleViewOptionsContext {
  surface: 'mobile' | 'desktop';
  clusterConfig: ResolvedClusterConfig | null;
}

const ZOOM_LABELS: Record<Exclude<ScheduleZoomOptionValue, 'inherit'>, string> = {
  today: 'Today',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

const LENS_LABELS: Record<Exclude<ScheduleLensOptionValue, 'inherit'>, string> = {
  calendar: 'Calendar',
  crew: 'Crew',
  route: 'Route',
};

// Cluster config fields are typed `string`; guard the label lookup so a value
// outside the known set degrades to `undefined` rather than an unsafe index.
function zoomLabel(value: string | undefined): string | undefined {
  return value && value in ZOOM_LABELS
    ? ZOOM_LABELS[value as Exclude<ScheduleZoomOptionValue, 'inherit'>]
    : undefined;
}

function lensLabel(value: string | undefined): string | undefined {
  return value && value in LENS_LABELS
    ? LENS_LABELS[value as Exclude<ScheduleLensOptionValue, 'inherit'>]
    : undefined;
}

export function getScheduleZoomOptions(
  ctx: ScheduleViewOptionsContext,
): ScheduleViewOption[] {
  const inheritedZoom = zoomLabel(ctx.clusterConfig?.defaultLandingZoom);

  const options: ScheduleViewOption[] = [
    {
      value: 'inherit',
      label: 'Match your industry',
      subtitle: inheritedZoom,
    },
    { value: 'day', label: ZOOM_LABELS.day },
    { value: 'week', label: ZOOM_LABELS.week },
    { value: 'month', label: ZOOM_LABELS.month },
  ];

  if (ctx.surface === 'mobile') {
    options.unshift({ value: 'today', label: ZOOM_LABELS.today });
    options.push({ value: 'year', label: ZOOM_LABELS.year });
  } else {
    options.push({ value: 'year', label: ZOOM_LABELS.year });
  }

  return options;
}

export function getScheduleLensOptions(
  ctx: ScheduleViewOptionsContext,
): ScheduleViewOption[] {
  const inheritedLens = lensLabel(ctx.clusterConfig?.defaultLandingLens);

  const options: ScheduleViewOption[] = [
    {
      value: 'inherit',
      label: 'Match your industry',
      subtitle: inheritedLens,
    },
    { value: 'calendar', label: LENS_LABELS.calendar },
  ];

  if (ctx.surface === 'desktop') {
    options.push({ value: 'crew', label: LENS_LABELS.crew });
  }

  const routeAvailable = ctx.clusterConfig?.showRouteLens === true;
  options.push({
    value: 'route',
    label: LENS_LABELS.route,
    disabled: !routeAvailable,
    disabledReason: routeAvailable
      ? undefined
      : 'Route lens is available for industries that run stops by address.',
  });

  return options;
}
